import type { Locale } from "@/lib/translations";

/**
 * The site's pages, as the one list the assistant is allowed to reason about.
 *
 * This registry backs three things that would otherwise each invent their own
 * notion of "a page of this site":
 *
 *   1. validating the `page` a request claims the visitor is on,
 *   2. resolving the URL a knowledge tool cites,
 *   3. bounding where a navigation handoff may point.
 *
 * Tools take a `RouteKey`, never a path. Tool arguments are model output, and
 * the Next.js documentation is explicit that untrusted URLs must not reach
 * `router.push` — `javascript:` URLs are executed in the page's context. A
 * closed key union means an invalid value is rejected before it reaches the
 * client, and the client resolves the key here rather than being handed a path.
 *
 * `src/app/_playbook/` is deliberately absent: an underscore-prefixed folder is
 * private in the App Router and is not routed in any mode, so it is not a page
 * of this site.
 */

export type RouteKey =
  | "home"
  | "contact"
  | "solutions"
  | "packages"
  | "ai-readiness"
  | "tech-radar"
  | "partners"
  | "blog"
  | "blog-post"
  | "projects"
  | "project"
  | "solutions-ai-automation"
  | "solutions-web-apps"
  | "solutions-mobile-apps"
  | "solutions-backend"
  | "solutions-devops"
  | "solutions-consulting"
  | "impressum"
  | "datenschutz"
  | "agb"
  | "nutzungsbedingungen";

export interface RouteEntry {
  readonly key: RouteKey;
  /** The address as the site serves it. */
  readonly path: string;
  /** What this page is, in a few words, for the model to read. */
  readonly label: Readonly<Record<Locale, string>>;
  /**
   * Whether the assistant may cite or offer this destination.
   *
   * A page can be a real route and still not be something to send a visitor to.
   * The project pages describe illustrative work rather than delivered
   * projects, and the two dynamic patterns have no address without a slug the
   * assistant has no way to know.
   */
  readonly offerable: boolean;
  /** Set for dynamic segments; matched against a claimed path. */
  readonly pattern?: RegExp;
}

export const ROUTES: readonly RouteEntry[] = [
  {
    key: "home",
    path: "/",
    label: { de: "Startseite", en: "Home" },
    offerable: true,
  },
  {
    key: "contact",
    path: "/#contact",
    label: { de: "Kontaktbereich", en: "Contact section" },
    offerable: true,
  },
  /*
   * Sections of the home page, not pages of their own. The overview has no
   * `/solutions` page — the model wrote that address when it had none to use —
   * and the packages are published only here.
   */
  {
    key: "solutions",
    path: "/#solutions",
    label: { de: "Übersicht aller Leistungen", en: "Overview of all services" },
    offerable: true,
  },
  {
    key: "packages",
    path: "/#packages",
    label: {
      de: "Pakete mit Startpreis und Dauer",
      en: "Packages with starting price and duration",
    },
    offerable: true,
  },
  {
    key: "ai-readiness",
    path: "/ai-readiness",
    label: {
      de: "KI-Readiness-Check — Selbsteinschätzung mit Auswertung",
      en: "AI readiness check — self-assessment with a scored result",
    },
    offerable: true,
  },
  {
    key: "tech-radar",
    path: "/tech-radar",
    label: {
      de: "Tech-Radar — eingesetzte und beobachtete Technologien",
      en: "Tech radar — technologies in use and under observation",
    },
    offerable: true,
  },
  {
    key: "partners",
    path: "/partners",
    label: { de: "Partner", en: "Partners" },
    offerable: true,
  },
  {
    key: "blog",
    path: "/blog",
    label: { de: "Blog", en: "Blog" },
    offerable: true,
  },
  {
    key: "blog-post",
    path: "/blog/[slug]",
    label: { de: "Blogbeitrag", en: "Blog post" },
    offerable: false,
    pattern: /^\/blog\/[^/]+$/,
  },
  {
    key: "projects",
    path: "/projects",
    label: { de: "Projekte", en: "Projects" },
    offerable: false,
  },
  {
    key: "project",
    path: "/projects/[slug]",
    label: { de: "Projektdetail", en: "Project detail" },
    offerable: false,
    pattern: /^\/projects\/[^/]+$/,
  },
  {
    key: "solutions-ai-automation",
    path: "/solutions/ai-automation",
    label: { de: "KI & Automation", en: "AI & Automation" },
    offerable: true,
  },
  {
    key: "solutions-web-apps",
    path: "/solutions/web-apps",
    label: { de: "Web-Applikationen", en: "Web applications" },
    offerable: true,
  },
  {
    key: "solutions-mobile-apps",
    path: "/solutions/mobile-apps",
    label: { de: "Mobile Apps", en: "Mobile apps" },
    offerable: true,
  },
  {
    key: "solutions-backend",
    path: "/solutions/backend",
    label: { de: "Backend-Systeme", en: "Backend systems" },
    offerable: true,
  },
  {
    key: "solutions-devops",
    path: "/solutions/devops",
    label: { de: "DevOps & Cloud", en: "DevOps & Cloud" },
    offerable: true,
  },
  {
    key: "solutions-consulting",
    path: "/solutions/consulting",
    label: { de: "Tech Consulting", en: "Tech consulting" },
    offerable: true,
  },
  {
    key: "impressum",
    path: "/impressum",
    label: { de: "Impressum", en: "Legal notice" },
    offerable: true,
  },
  {
    key: "datenschutz",
    path: "/datenschutz",
    label: { de: "Datenschutzerklärung", en: "Privacy policy" },
    offerable: true,
  },
  {
    key: "agb",
    path: "/agb",
    label: { de: "AGB", en: "Terms and conditions" },
    offerable: true,
  },
  {
    key: "nutzungsbedingungen",
    path: "/nutzungsbedingungen",
    label: {
      de: "Nutzungsbedingungen für den KI-Assistenten",
      en: "Terms of use for the AI assistant",
    },
    /*
     * Recognised, never proposed. The assistant knowing which page it is
     * standing on is useful; the assistant offering to send a visitor to the
     * rules governing their use of it is not the next step anyone came for.
     * The link that matters is the one under the composer, which is always
     * there — see AssistantPanel.
     */
    offerable: false,
  },
] as const;

/**
 * The site's canonical origin, for turning a path into a full address.
 *
 * The same literal appears in `src/app/sitemap.ts`, `src/app/robots.ts` and
 * `src/app/blog/[slug]/page.tsx`; those predate this file and are left alone.
 */
export const SITE_URL = "https://haintz.dev";

const BY_KEY = new Map<RouteKey, RouteEntry>(ROUTES.map((r) => [r.key, r]));

/** Keys the assistant may cite or offer as a destination. */
export const OFFERABLE_ROUTE_KEYS: readonly RouteKey[] = ROUTES.filter(
  (r) => r.offerable,
).map((r) => r.key);

export function resolveRoute(key: string): RouteEntry | null {
  return BY_KEY.get(key as RouteKey) ?? null;
}

export function resolvePath(key: string): string | null {
  return resolveRoute(key)?.path ?? null;
}

/** The full, externally valid address of a page. */
export function resolveUrl(key: string): string | null {
  const path = resolvePath(key);
  return path === null ? null : `${SITE_URL}${path === "/" ? "" : path}`;
}

/** Whether a value is a key the assistant is allowed to offer. */
export function isOfferableKey(value: unknown): value is RouteKey {
  return (
    typeof value === "string" &&
    OFFERABLE_ROUTE_KEYS.includes(value as RouteKey)
  );
}

/**
 * A site path written in prose, optionally with the domain in front of it.
 *
 * Shared by the panel, which turns each one into a link, and by the route,
 * which offers the page when an answer names exactly one. Keeping the pattern
 * here is what stops the two drifting into disagreeing about what counts.
 */
const SITE_REFERENCE =
  /(?:https?:\/\/(?:www\.)?haintz\.dev)?(\/[a-z0-9][a-z0-9-]*(?:\/[a-z0-9][a-z0-9-]*)*)/gi;

/**
 * Every page of this site that a piece of text names, in order, deduplicated.
 *
 * Only static routes are returned. A dynamic one like `/blog/[slug]` matches
 * its pattern without the slug having to exist, so treating it as a real page
 * would risk offering a link to a 404.
 */
export function sitePathsIn(text: string): RouteEntry[] {
  const found: RouteEntry[] = [];
  for (const match of text.matchAll(SITE_REFERENCE)) {
    const entry = matchPath(match[1]);
    if (!entry || entry.pattern) continue;
    if (!found.some((e) => e.key === entry.key)) found.push(entry);
  }
  return found;
}

/**
 * Resolve a path the client claims the visitor is on.
 *
 * Returns a registry entry or nothing. It never returns, echoes, or derives a
 * value from the caller's string: everything downstream reads the entry's own
 * label, so an unrecognised path simply contributes nothing. A dynamic route
 * matches its pattern, which means a blog slug identifies the *kind* of page
 * without the slug itself ever reaching a prompt.
 */
export function matchPath(claimed: unknown): RouteEntry | null {
  if (typeof claimed !== "string") return null;

  // A path, and only a path. Rejecting anything that does not start with a
  // single slash excludes every scheme, including `javascript:`, and excludes
  // protocol-relative `//host` addresses.
  if (!claimed.startsWith("/") || claimed.startsWith("//")) return null;

  // A registered section anchor is its own entry. Any other fragment is
  // dropped below, and the page it sits on is what matches.
  const anchor = ROUTES.find((r) => r.path.includes("#") && r.path === claimed);
  if (anchor) return anchor;

  const withoutFragment = claimed.split(/[?#]/)[0];
  const normalised =
    withoutFragment.length > 1 && withoutFragment.endsWith("/")
      ? withoutFragment.slice(0, -1)
      : withoutFragment;

  for (const route of ROUTES) {
    if (!route.pattern && route.path === normalised) return route;
  }
  for (const route of ROUTES) {
    if (route.pattern?.test(normalised)) return route;
  }
  return null;
}
